// THE single place money is formatted for display — format for display in
// exactly one place. It lives here (rather than
// `@sportsmet/ui`, which doesn't exist yet at this build phase) so every
// early package has one canonical import instead of five call sites each
// hand-rolling `(cents / 100).toFixed(2)`. When `@sportsmet/ui` ships, it
// re-exports from here — it does not reimplement.
//
// Every monetary value on this platform is USD, stored and passed around as
// an integer count of cents. There is no other representation, ever. Both
// functions below throw on a non-integer input
// specifically to catch a float-money bug at the first call site that
// introduces one, rather than silently rendering `$12.499999999999998`.

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

// Cents with no fractional cent, and no currency other than USD, ever enter
// or leave this module.
function assertIntegerCents(cents: number): number {
  if (!Number.isInteger(cents)) {
    throw new TypeError(
      `formatCents expects an integer count of USD cents, got ${cents}. Money is never a float.`,
    );
  }
  return cents;
}

/**
 * Format an integer count of USD cents for display: `1250` -> `"$12.50"`,
 * `-500` -> `"-$5.00"`, `0` -> `"$0.00"`.
 */
export function formatCents(cents: number): string {
  return USD_FORMATTER.format(assertIntegerCents(cents) / 100);
}

/**
 * Parse a user-typed dollar amount (`"12.50"`, `"$12.50"`, `"12"`) into
 * integer cents. Rejects anything that is not a plain USD decimal — no
 * thousands separators, no other currency symbol, at most two decimal
 * places. Throws `TypeError` on anything it can't parse unambiguously,
 * rather than silently rounding a typo.
 */
export function parseDollarsToCents(input: string): number {
  const trimmed = input.trim();
  const match = /^\$?(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  if (!match) {
    throw new TypeError(
      `"${input}" is not a valid USD amount (expected e.g. "12.50" or "$12.50").`,
    );
  }
  const [, wholeDollars, fractionalCents] = match;
  const cents = Number(fractionalCents ? fractionalCents.padEnd(2, "0") : "00");
  return Number(wholeDollars) * 100 + cents;
}
